import React, { useEffect, useRef, useState } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import {
  Button, Drawer, DrawerHeader
} from '@one-thd/sui-atomic-components';
import { triggerAnalytics } from '../helpers';
import {
  PROJECT_CREATE_BUTTON_CLICKED,
  PROJECT_CREATE_OVERLAY,
  PROJECTS_INTERACTION
} from '../constants';
import { CreateAProjectContent } from './CreateAProjectContent';

const { func, string, bool, oneOf } = proptypes;

export const CreateAProject = (props) => {

  const {
    CustomCreateProjectActionElement,
    onCreate,
    message,
    showSuccessScreen,
    onClose,
    actionAlignment = 'center',
    isCartExp
  } = props;

  const createProjectBtnRef = useRef(null);
  const hasCompletedSubmissionRef = useRef(false);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('CreateAProject.ready');
  }, []);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open && createProjectBtnRef.current) {
      createProjectBtnRef.current.blur();
    }

    if (open) {
      triggerAnalytics(PROJECT_CREATE_OVERLAY, {});
    }
  }, [open]);

  const handleOpen = () => {
    hasCompletedSubmissionRef.current = false;
    setOpen(true);
    triggerAnalytics(PROJECT_CREATE_BUTTON_CLICKED, {});
  };

  const handleClose = () => {
    if (!hasCompletedSubmissionRef.current) {
      triggerAnalytics(PROJECTS_INTERACTION, { action: 'close-create-new-project-overlay' });
    }
    setOpen(false);
    onClose();
  };

  const handleCreate = (createdProject) => {
    hasCompletedSubmissionRef.current = true;
    onCreate(createdProject);
  };

  const divCss = classNames(
    'sui-flex',
    { 'sui-justify-center': actionAlignment === 'center' },
    { 'sui-justify-start': actionAlignment === 'left' }
  );

  return (
    <div className={divCss} data-component="CreateAProject">
      {CustomCreateProjectActionElement
        ? <CustomCreateProjectActionElement onClick={handleOpen} ref={createProjectBtnRef} />
        : (
          <Button
            fullWidth
            variant="primary"
            onClick={handleOpen}
            aria-label="Create Project"
            data-testid="create-new-project"
            ref={createProjectBtnRef}
          >
            Create Project
          </Button>
        )}
      <Drawer open={open} onClose={handleClose}>
        <DrawerHeader title="Create Project" onClose={handleClose} />
        <CreateAProjectContent
          onCreate={handleCreate}
          handleClose={handleClose}
          message={message}
          showSuccessScreen={showSuccessScreen}
          isCartExp={isCartExp}
        />
      </Drawer>
    </div>
  );
};

CreateAProject.propTypes = {
  // callback when user clicks the form button
  // return parameters: { id, projectAccessType, project }
  onCreate: func.isRequired,
  CustomCreateProjectActionElement: func,
  // optional informational message shown as an Alert at the top of the drawer
  // (e.g. gating copy when the drawer is opened to unlock a downstream capability)
  message: string,
  // when true, show an in-drawer success screen with an "Ok" button instead of auto-closing
  showSuccessScreen: bool,
  // called whenever the drawer closes, whether by cancel, X, or the success screen's Ok
  onClose: func,
  actionAlignment: oneOf(['center', 'left']),
  // when true, show the in-cart success copy on the success screen
  isCartExp: bool
};

CreateAProject.defaultProps = {
  CustomCreateProjectActionElement: null,
  message: '',
  showSuccessScreen: false,
  onClose: () => {},
  actionAlignment: 'center',
  isCartExp: false
};

CreateAProject.displayName = 'CreateAProject';

CreateAProject.dataModel = extend(CreateAProjectContent);
